// LeagueClassicPage.js
// Renders the Classic Players tab content + a slide-up player modal
// Usage: new LeagueClassicPage(containerEl, leagueId).init()

import { CLASSIC_PLAYERS } from '../../data/classicPlayers.js';

const POS_COLOR = {
  GK:'#f59e0b', CB:'#3b82f6', LB:'#6366f1', RB:'#6366f1',
  DF:'#6366f1', MF:'#10b981', LW:'#ec4899', RW:'#ec4899',
  FW:'#ef4444', ST:'#ef4444', 'CB/MF':'#3b82f6', 'MF/FW':'#10b981',
  'RB/MF':'#6366f1', 'LB/MF':'#6366f1', 'QB/FW':'#ef4444',
};

function posColor(p) { return POS_COLOR[p] || '#6b7280'; }

const FLAG = {
  England:'🏴󠁧󠁢󠁥󠁮󠁧󠁿', USA:'🇺🇸', France:'🇫🇷', Spain:'🇪🇸', Germany:'🇩🇪',
  Italy:'🇮🇹', Sweden:'🇸🇪', Australia:'🇦🇺', Brazil:'🇧🇷', Japan:'🇯🇵',
  Portugal:'🇵🇹', 'South Korea':'🇰🇷', Ghana:'🇬🇭', Nigeria:'🇳🇬',
  Nepal:'🇳🇵', India:'🇮🇳', Bangladesh:'🇧🇩', Bhutan:'🇲🇽', 'USA/Mexico':'🇲🇽',
};

function flag(nat) { return FLAG[nat] || '🌍'; }

export class LeagueClassicPage {
  constructor(el, leagueId) {
    this.el  = typeof el === 'string' ? document.getElementById(el) : el;
    this.lid = leagueId;
    this.players = CLASSIC_PLAYERS[leagueId] || [];
    this._modal  = null;
  }

  init() {
    if (!this.el) return;
    if (!this.players.length) {
      this.el.innerHTML = `<div style="padding:60px 20px;text-align:center;color:var(--text-secondary,#888);">
        <div style="font-size:40px;margin-bottom:12px;">🏆</div>
        <div style="font-weight:600;font-size:15px;margin-bottom:6px;">Classic Players coming soon</div>
        <div style="font-size:13px;">We're building the all-time XI for this league.</div>
      </div>`;
      return;
    }
    this._render();
    this._ensureModal();
  }

  _render() {
    this.el.innerHTML = `
      <div style="padding:16px 0;">
        <div style="font-size:11px;font-weight:700;letter-spacing:.08em;text-transse;
                    color:var(--text-secondary,#888);padding:0 4px 12px;opacity:.7;">
          ${this.players.length} iconic players — tap for profile
        </div>
        <div id="cp-grid-${this.lid}" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(148px,1fr));gap:10px;"></div>
      </div>`;
    const grid = document.getElementById(`cp-grid-${this.lid}`);
    grid.innerHTML = this.players.map((p, i) => this._card(p, i)).join('');
    grid.onclick = e => {
      const c = e.target.closest('[data-pi]');
      if (c) this._openModal(this.players[+c.dataset.pi]);
    };
  }

  _card(p, i) {
    const col = posColor(p.pos);
    return `
      <div data-pi="${i}" style="
        background:var(--card-bg,#fff);border:1px solid var(--border-color,#e5e7eb);
        border-radius:12px;padding:14px 12px;cursor:pointer;transition:transform .15s,box-shadow .15s;
        position:relative;overflow:hidden;"
        onmouseenter="this.style.transform='translateY(-2px)';this.style.boxShadow='0 8 24px rgba(0,0,0,.10)'"
        onmouseleave="this.style.transform='';this.style.boxShadow=''">
        <div style="position:absolute;top:0;left:0;right:0;height:3px;background:${col};border-radius:12px 12px 0 0;"></div>
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px;">
          <span style="font-size:20px;line-height:1;">${flag(p.nat)}</span>
          <span style="font-size:10px;font-weight:700;color:${col};background:${col}18;
                       padding:2px 6px;border-radius:20px;letter-spacing:.04em;">${p.pos}</span>
        </div>
        <div style="font-size:13px;font-weight:700;color:var(--text-color,#111);line-height:1.3;margin-bottom:3px;">${p.name}</div>
        <div style="font-size:11px;color:var(--text-secondary,#777);margin-bottom:6px;">${p.years}</div>
        <div style="font-size:10px;color:var(--text-secondary,#999);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${p.clubs}</div>
      </div>`;
  }

  _ensureModal() {
    if (document.getElementById('cp-modal-overlay')) return;
    const ov = document.createElement('div');
    ov.id = 'cp-modal-overlay';
    ov.style.cssText = `
      display:none;position:fixed;inset:0;z-index:9999;
      background:rgba(0,0,0,.45);backdrop-filter:blur(4px);
      -webkit-backdrop-filter:blur(4px);align-items:flex-end;justify-content:center;`;
    ov.innerHTML = `
      <div id="cp-modal-sheet" style="
        background:var(--background-color,#fff);border-radius:24px 24px 0 0;
        width:100%;max-width:540px;padding:28px 24px 40px;
        transform:translateY(100%);transition:transform .3s cubic-bezier(.32,.72,0,1);
        max-height:80dvh;overflow-y:auto;">
        <div style="width:40px;height:4px;background:var(--border-color,#d1d5db);border-radius:2px;margin:0 auto 20px;"></div>
        <div id="cp-modal-body"></div>
      </div>`;
    document.body.appendChild(ov);
    ov.addEventListener('click', e => { if (e.target === ov) this._closeModal(); });
    this._modal = ov;
  }

  _openModal(p) {
    const col = posColor(p.pos);
    document.getElementById('cp-modal-body').innerHTML = `
      <div style="display:flex;align-items:flex-start;gap:12px;margin-bottom:16px;">
        <div style="font-size:44px;line-height:1;">${flag(p.nat)}</div>
        <div style="flex:1;">
          <div style="font-size:20px;font-weight:800;color:var(--text-color,#111);line-height:1.2;margin-bottom:4px;">${p.name}</div>
          <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:4px;">
            <span style="font-size:11px;font-weight:700;color:${col};background:${col}18;padding:3px 8px;border-radius:20px;">${p.pos}</span>
            <span style="font-size:11px;color:var(--text-secondary,#777);padding:3px 8px;background:var(--border-color,#f3f4f6);border-radius:20px;">${p.nat}</span>
          </div>
        </div>
      </div>
      <div style="background:var(--surface-color,#f9fafb);border-radius:12px;padding:14px;margin-bottom:16px;">
        <div style="font-size:11px;font-weight:700;color:var(--text-secondary,#888);text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;">Career</div>
        <div style="display:flex;justify-content:space-between;flex-wrap:wrap;gap:8px;">
          <div>
            <div style="font-size:10px;color:var(--text-secondary,#999);margin-bottom:2px;">Active years</div>
            <div style="font-size:14px;font-weight:600;color:var(--text-color,#111);">${p.years}</div>
          </div>
        </div>
        <div style="margin-top:10px;">
          <div style="font-size:10px;color:var(--text-secondary,#999);margin-bottom:4px;">Clubs</div>
          <div style="font-size:13px;color:var(--text-color,#111);line-height:1.6;">${p.clubs}</div>
        </div>
      </div>
      <div style="font-size:14px;line-height:1.7;color:var(--text-color,#333);">${p.bio}</div>`;
    const ov = document.getElementById('cp-modal-overlay');
    ov.style.display = 'flex';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById('cp-modal-sheet').style.transform = 'translateY(0)';
      });
    });
  }

  _closeModal() {
    const sheet = document.getElementById('cp-modal-sheet');
    const ov    = document.getElementById('cp-modal-overlay');
    if (!sheet) return;
    sheet.style.transform = 'translateY(100%)';
    setTimeout(() => { ov.style.display = 'none'; }, 320);
  }
}
